import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import { FRACTION_ZERO, fractionSum } from "@aprao/common/src/math/fraction.js";
import { sum } from "@aprao/common/src/math/sum.js";
import type { FundingType } from "@aprao/common/src/shapes/common.js";
import { times } from "@aprao/common/src/util/times.js";
import { makeAutoObservable } from "mobx";
import { computedFn } from "mobx-utils";

import { salesGroupPredicate } from "../shapes/ShapeRevenueGroup.js";
import type { AppraisalDocumentFacade } from "./AppraisalDocumentFacade.js";
import type { BuildPhaseFacade } from "./build/BuildPhaseFacade.js";
import { computeAnnualIrr } from "./computeAnnualIrr.js";
import type { WithProjectionPeriodsDelegate } from "./delegates/WithProjectionPeriodsDelegate.js";
import type { WithUncappedProjectionPeriodsDelegate } from "./delegates/WithUncappedProjectionPeriodsDelegate.js";
import type { EscalatedAmountFacade } from "./EscalatedAmountFacade.js";
import type { OtherCostFacade } from "./OtherCostsGroupFacade.js";
import type { ResidualFacade } from "./ResidualFacade.js";
import type { SitePriceManager } from "./SitePriceManager.js";
import type { CustomCostFacade } from "./SitePurchaseFacade.js";
import type { TaxFacade } from "./TaxFacade.js";

interface WithProjection {
  projectionPeriods:
    | WithProjectionPeriodsDelegate
    | WithUncappedProjectionPeriodsDelegate;
}

export const computeProjectionAmount = (
  projectionPeriods:
    | WithProjectionPeriodsDelegate
    | WithUncappedProjectionPeriodsDelegate,
  total: BigFraction | null,
  periodIndex: number,
) => {
  const portion = projectionPeriods.getPortionAtPeriodIndex(periodIndex);
  return portion == null || total == null ? null : portion.mul(total);
};

export const sumProjectionAmountAtPeriodIndex = (
  storeTotalTuples: ReadonlyArray<[WithProjection, BigFraction | null]>,
  periodIndex: number,
) => {
  let periodTotal = FRACTION_ZERO;

  for (const [store, total] of storeTotalTuples) {
    periodTotal = periodTotal.add(
      computeProjectionAmount(store.projectionPeriods, total, periodIndex) ??
        FRACTION_ZERO,
    );
  }

  return periodTotal;
};

/**
 * Builds an array of the sum of projection's resolved amount indexed by period.
 */
const _sumProjectionAmountAtPeriodIndex = (
  storeTotalTuples: ReadonlyArray<[WithProjection, BigFraction | null]>,
  duration: number,
) =>
  times(duration, (periodIndex) =>
    sumProjectionAmountAtPeriodIndex(storeTotalTuples, periodIndex),
  );

const sumMainCostsProjectionAtPeriodIndex = (
  phases: ReadonlyArray<BuildPhaseFacade>,
  periodIndex: number,
): Record<FundingType, BigFraction> => {
  const totals = {
    debtFunded: FRACTION_ZERO,
    forwardFunded: FRACTION_ZERO,
  };

  for (const phase of phases) {
    const { fundingType } = phase;

    const projectionAmount = computeProjectionAmount(
      phase.projectionPeriods,
      phase.costTotalWithContingency,
      periodIndex,
    );

    totals[fundingType] = totals[fundingType].add(
      projectionAmount ?? FRACTION_ZERO,
    );
  }

  return totals;
};

/**
 * Regroups all the views and computations used for the cashflow.
 */
export class CashflowFacade {
  $stores;

  constructor(stores: {
    documentFacade: AppraisalDocumentFacade;
    taxFacade: TaxFacade;
    priceManager: SitePriceManager;
    residualFacade: ResidualFacade;
  }) {
    makeAutoObservable(this);

    this.$stores = stores;
  }

  get _periodsDurations() {
    return [
      ...this.$stores.documentFacade.escalatedIncomes,
      ...this.$stores.documentFacade.sitePurchases.flatMap((site) => [
        site.pricePurchase,
        this.$stores.residualFacade.getLandTaxBySite(site),
        ...this.$stores.residualFacade.getCustomCostsBySite(site),
      ]),
      this.$stores.documentFacade.buildCostsPreliminary,
      ...this.$stores.documentFacade.buildCostsPhases,
      ...this.$stores.documentFacade.otherCostsGroups.flatMap(
        ({ costs }) => costs,
      ),
      ...this.$stores.documentFacade.escalatedCosts,
      ...this.$stores.residualFacade.lenders.flatMap(({ fees }) => fees),
    ];
  }

  get projectionDuration() {
    return Math.max(
      // the revenue groups need to treated differently
      ...this.$stores.documentFacade.revenueGroups.map(
        ({ projectionDuration }) => projectionDuration,
      ),
      ...this._periodsDurations.map(
        ({ projectionPeriods }) => projectionPeriods.duration,
      ),
    );
  }

  getGrossProceedsProjectionAtPeriodIndex = (
    periodIndex: number,
  ): Record<FundingType, BigFraction> => {
    const totals = {
      debtFunded: FRACTION_ZERO,
      forwardFunded: FRACTION_ZERO,
    };

    for (const group of this.$stores.documentFacade.revenueGroups) {
      const { fundingType } = group;
      totals[fundingType] = totals[fundingType].add(
        group.getGrossProceedsProjectionAtPeriodIndex(periodIndex),
      );
    }

    return totals;
  };

  get grossProceedsProjectionAggregated() {
    return fractionSum(
      times(
        this.projectionDuration,
        this.getGrossProceedsProjectionAtPeriodIndex,
      ).flatMap(({ debtFunded, forwardFunded }) => [debtFunded, forwardFunded]),
    );
  }

  getSalesCostProjectionAtPeriodIndex = (
    periodIndex: number,
  ): Record<FundingType, BigFraction> => {
    const totals = {
      debtFunded: FRACTION_ZERO,
      forwardFunded: FRACTION_ZERO,
    };

    for (const group of this.$stores.documentFacade.revenueGroups) {
      if (salesGroupPredicate(group)) {
        const { fundingType } = group;
        totals[fundingType] = totals[fundingType].add(
          group.getSalesCostProjectionAtPeriodIndex(periodIndex),
        );
      }
    }

    return totals;
  };

  get salesCostProjectionAggregated() {
    return fractionSum(
      times(
        this.projectionDuration,
        this.getSalesCostProjectionAtPeriodIndex,
      ).flatMap(({ debtFunded, forwardFunded }) => [debtFunded, forwardFunded]),
    );
  }

  getSalesTaxProjectionAtPeriodIndex = (
    periodIndex: number,
  ): Record<FundingType, BigFraction> => {
    const proceeds = this.getGrossProceedsProjectionAtPeriodIndex(periodIndex);

    return {
      debtFunded: proceeds.debtFunded.mul(
        this.$stores.taxFacade.gdvEffectiveSalesTaxRate ?? FRACTION_ZERO,
      ),
      forwardFunded: proceeds.forwardFunded.mul(
        this.$stores.taxFacade.gdvEffectiveSalesTaxRate ?? FRACTION_ZERO,
      ),
    };
  };

  get salesTaxProjectionAggregated() {
    return fractionSum(
      times(
        this.projectionDuration,
        this.getSalesTaxProjectionAtPeriodIndex,
      ).flatMap(({ debtFunded, forwardFunded }) => [debtFunded, forwardFunded]),
    );
  }

  getEscalatedIncomesCombinedAtPeriodIndex = (periodIndex: number) =>
    sumProjectionAmountAtPeriodIndex(
      this.$stores.documentFacade.escalatedIncomes.map(
        (cost): [EscalatedAmountFacade, BigFraction | null] => [
          cost,
          cost.valueHolder.value,
        ],
      ),
      periodIndex,
    );

  get escalatedIncomesCombinedAggregated() {
    return fractionSum(
      times(
        this.projectionDuration,
        this.getEscalatedIncomesCombinedAtPeriodIndex,
      ),
    );
  }

  getNetProceedsProjectionAtPeriodIndex = (
    periodIndex: number,
  ): Record<FundingType, BigFraction> => {
    const proceeds = this.getGrossProceedsProjectionAtPeriodIndex(periodIndex);

    const escalatedIncome =
      this.getEscalatedIncomesCombinedAtPeriodIndex(periodIndex);

    const salesCost = this.getSalesCostProjectionAtPeriodIndex(periodIndex);
    const salesTax = this.getSalesTaxProjectionAtPeriodIndex(periodIndex);

    return {
      debtFunded: proceeds.debtFunded
        .add(escalatedIncome)
        .sub(salesCost.debtFunded)
        .sub(salesTax.debtFunded),
      forwardFunded: proceeds.forwardFunded
        .sub(salesCost.forwardFunded)
        .sub(salesTax.forwardFunded),
    };
  };

  get netProceedsProjectionAggregated() {
    return fractionSum(
      times(
        this.projectionDuration,
        this.getNetProceedsProjectionAtPeriodIndex,
      ).flatMap(({ debtFunded, forwardFunded }) => [debtFunded, forwardFunded]),
    );
  }

  getPurchasesCostProjectionAtPeriodIndex = (periodIndex: number) => {
    const totalPurchase = sumProjectionAmountAtPeriodIndex(
      this.$stores.documentFacade.sitePurchases.map((site) => [
        site.pricePurchase,
        this.$stores.priceManager.getPriceBySite(site),
      ]),
      periodIndex,
    );

    const totalCustomCosts = sumProjectionAmountAtPeriodIndex(
      this.$stores.documentFacade.sitePurchases.flatMap((site) =>
        this.$stores.residualFacade
          .getCustomCostsBySite(site)
          .map((cost): [CustomCostFacade, BigFraction | null] => [
            cost,
            cost.relativesSet.getRelative("absolute"),
          ]),
      ),
      periodIndex,
    );

    const totalStampDuty = sumProjectionAmountAtPeriodIndex(
      this.$stores.documentFacade.sitePurchases.map((site) => {
        const landTax = this.$stores.residualFacade.getLandTaxBySite(site);
        return [landTax, landTax.landTax];
      }),
      periodIndex,
    );

    return totalPurchase.add(totalCustomCosts).add(totalStampDuty);
  };

  get purchasesCostProjectionAggregated() {
    return fractionSum(
      times(
        this.projectionDuration,
        this.getPurchasesCostProjectionAtPeriodIndex,
      ),
    );
  }

  getBuildCostProjectionAtPeriodIndex = (
    periodIndex: number,
  ): Record<FundingType, BigFraction> => {
    const mainCost = sumMainCostsProjectionAtPeriodIndex(
      this.$stores.documentFacade.buildCostsPhases,
      periodIndex,
    );

    const preliminaryCost = computeProjectionAmount(
      this.$stores.documentFacade.buildCostsPreliminary.projectionPeriods,
      this.$stores.documentFacade.buildCostsPreliminary.totalCost,
      periodIndex,
    );

    return {
      ...mainCost,
      debtFunded: mainCost.debtFunded.add(preliminaryCost ?? FRACTION_ZERO),
    };
  };

  get buildCostProjectionAggregated() {
    return fractionSum(
      times(
        this.projectionDuration,
        this.getBuildCostProjectionAtPeriodIndex,
      ).flatMap(({ debtFunded, forwardFunded }) => [debtFunded, forwardFunded]),
    );
  }

  getOtherCostTotalProjectionAtPeriodIndex = (periodIndex: number) =>
    sumProjectionAmountAtPeriodIndex(
      this.$stores.documentFacade.otherCostsGroups.flatMap(({ costs }) =>
        costs.map((cost): [OtherCostFacade, BigFraction | null] => [
          cost,
          cost.relativesSet.getRelative("absolute"),
        ]),
      ),
      periodIndex,
    );

  get otherCostProjectionAggregated() {
    return fractionSum(
      times(
        this.projectionDuration,
        this.getOtherCostTotalProjectionAtPeriodIndex,
      ),
    );
  }

  getEscalatedAmountAggregated = (escalatedAmount: EscalatedAmountFacade) =>
    sum(
      _sumProjectionAmountAtPeriodIndex(
        [[escalatedAmount, escalatedAmount.valueHolder.value]],
        this.projectionDuration,
      ),
    );

  getEscalatedCostsCombinedAtPeriodIndex = (periodIndex: number) =>
    sumProjectionAmountAtPeriodIndex(
      this.$stores.documentFacade.escalatedCosts.map(
        (cost): [EscalatedAmountFacade, BigFraction | null] => [
          cost,
          cost.valueHolder.value,
        ],
      ),
      periodIndex,
    );

  get escalatedCostsCombinedAggregated() {
    return fractionSum(
      times(
        this.projectionDuration,
        this.getEscalatedCostsCombinedAtPeriodIndex,
      ),
    );
  }

  getDevelopmentCostProjectionAtPeriodIndex = (
    periodIndex: number,
  ): Record<FundingType, BigFraction> => {
    const sitePurchaseCost =
      this.getPurchasesCostProjectionAtPeriodIndex(periodIndex);
    const buildCost = this.getBuildCostProjectionAtPeriodIndex(periodIndex);

    return {
      debtFunded: sitePurchaseCost
        .add(buildCost.debtFunded)
        .add(this.getOtherCostTotalProjectionAtPeriodIndex(periodIndex))
        .add(this.getEscalatedCostsCombinedAtPeriodIndex(periodIndex)),
      forwardFunded: buildCost.forwardFunded,
    };
  };

  get developmentCostProjectionAggregated() {
    return fractionSum(
      times(
        this.projectionDuration,
        this.getDevelopmentCostProjectionAtPeriodIndex,
      ).flatMap(({ debtFunded, forwardFunded }) => [debtFunded, forwardFunded]),
    );
  }

  getNetCashFlowsProjectionAtPeriodIndex = computedFn(
    // a 2x optimization since this runs at least twice on user input
    (periodIndex: number): Record<FundingType, BigFraction> => {
      const income = this.getNetProceedsProjectionAtPeriodIndex(periodIndex);

      const developmentCost =
        this.getDevelopmentCostProjectionAtPeriodIndex(periodIndex);

      return {
        debtFunded: income.debtFunded.sub(developmentCost.debtFunded),
        forwardFunded: income.forwardFunded.sub(developmentCost.forwardFunded),
      };
    },
  );

  get netCashFlowProjectionAggregated() {
    return fractionSum(
      times(
        this.projectionDuration,
        this.getNetCashFlowsProjectionAtPeriodIndex,
      ).flatMap(({ debtFunded, forwardFunded }) => [debtFunded, forwardFunded]),
    );
  }

  /**
   * The calculation assumes that no debt is used for the project and is
   * therefore based on the net cash flow of each period.
   */
  get projectIrr() {
    return computeAnnualIrr(
      times(
        this.projectionDuration,
        this.getNetCashFlowsProjectionAtPeriodIndex,
      ).map(({ debtFunded, forwardFunded }) => debtFunded.add(forwardFunded)),
      this.$stores.documentFacade.cashflowPeriodGranularity,
    );
  }
}
